/**
 * KR // Zoom Dialog Navigation
 *
 * Replaces the default scroll-the-scrollbar behaviour with:
 *   Desktop  — mouse wheel / trackpad scroll  → snap to next / prev image
 *   Mobile   — horizontal swipe              → next / prev image
 *              pinch / double-tap            → zoom within the image (existing)
 *
 * Works alongside zoom-dialog.js and drag-zoom-wrapper.js without modifying them.
 */
(function () {
  'use strict';

  /* ── Config ──────────────────────────────────────────────────────────── */
  const SWIPE_MIN_PX   = 60;   // minimum horizontal distance to count as a swipe
  const SWIPE_RATIO    = 1.3;  // horizontal must be this much larger than vertical
  const NAV_COOLDOWN   = 370;  // ms between navigations (prevents over-scroll firing)

  /* ── State ──────────────────────────────────────────────────────────── */
  let navigating  = false;
  let touchStartX = 0;
  let touchStartY = 0;

  /* ── Helpers ─────────────────────────────────────────────────────────── */

  function openDialog() {
    return document.querySelector('zoom-dialog dialog[open]');
  }

  function zoomEl() {
    return /** @type {any} */ (document.querySelector('zoom-dialog'));
  }

  /**
   * Read the current image index from thumbnail aria-selected attributes.
   * Falls back to 0 if nothing is found.
   */
  function currentIndex() {
    const el = zoomEl();
    if (!el) return 0;
    const btns = el.querySelectorAll('button[aria-selected]');
    for (let i = 0; i < btns.length; i++) {
      if (btns[i].getAttribute('aria-selected') === 'true') return i;
    }
    return 0;
  }

  function total() {
    const el = zoomEl();
    if (!el) return 0;
    return el.querySelectorAll('button[aria-selected]').length;
  }

  function goTo(index) {
    if (navigating) return;

    const el = zoomEl();
    if (!el || typeof el.selectThumbnail !== 'function') return;

    const count   = total();
    if (count === 0) return;

    const clamped = Math.max(0, Math.min(count - 1, index));
    if (clamped === currentIndex()) return;

    navigating = true;
    el.selectThumbnail(clamped, { behavior: 'smooth' });
    setTimeout(() => { navigating = false; }, NAV_COOLDOWN);
  }

  /* ── Desktop: wheel → image navigation ──────────────────────────────── */

  window.addEventListener('wheel', function (e) {
    if (!openDialog()) return;

    // Prevent the dialog from scrolling its own scrollbar
    e.preventDefault();
    e.stopPropagation();

    if (navigating) return;

    // Support both vertical scroll wheels and horizontal trackpad swipes
    const delta = Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
    goTo(currentIndex() + (delta > 0 ? 1 : -1));

  }, { passive: false, capture: true });

  /* ── Mobile: horizontal swipe → image navigation ────────────────────── */
  /*
   * DragZoomWrapper calls preventDefault on touchstart (so the browser's
   * native scroll is already blocked).  We listen at the window level on
   * touchend — which still fires — and detect a deliberate horizontal swipe.
   */

  window.addEventListener('touchstart', function (e) {
    if (!openDialog()) return;
    const t = e.touches[0];
    if (!t) return;
    touchStartX = t.clientX;
    touchStartY = t.clientY;
  }, { passive: true });

  window.addEventListener('touchend', function (e) {
    if (!openDialog()) return;
    const t = e.changedTouches[0];
    if (!t) return;

    const dx = t.clientX - touchStartX;
    const dy = t.clientY - touchStartY;

    const isHorizontal = Math.abs(dx) >= SWIPE_MIN_PX &&
                         Math.abs(dx) >= Math.abs(dy) * SWIPE_RATIO;

    if (!isHorizontal) return;

    // Swipe left → next image, swipe right → previous
    goTo(currentIndex() + (dx < 0 ? 1 : -1));

  }, { passive: true });

})();

/**
 * KR // Product Gallery Auto-Slide
 *
 * On the product detail page, automatically cycles through the main gallery
 * images every 4 seconds. Pauses when the user hovers and resumes on leave.
 * Stops permanently once the user manually clicks a thumbnail.
 */
(function () {
  'use strict';

  const INTERVAL_MS  = 4000;
  const PAUSE_MS     = 8000; // after hover-leave, wait before resuming

  let timer        = null;
  let pauseTimer   = null;
  let userActed    = false;

  function gallery() {
    return document.querySelector('.product-information__media media-gallery');
  }

  function thumbnails() {
    const g = gallery();
    return g ? Array.from(g.querySelectorAll('.media-gallery__thumbnail')) : [];
  }

  function currentThumbIndex() {
    return thumbnails().findIndex(
      btn => btn.getAttribute('aria-current') === 'true' ||
             btn.classList.contains('is-active') ||
             btn.getAttribute('aria-selected') === 'true'
    );
  }

  function advance() {
    const thumbs = thumbnails();
    if (thumbs.length < 2) return;
    const next = (Math.max(0, currentThumbIndex()) + 1) % thumbs.length;
    thumbs[next].click();
  }

  function startTimer() {
    if (userActed) return;
    clearInterval(timer);
    timer = setInterval(advance, INTERVAL_MS);
  }

  function stopTimer() {
    clearInterval(timer);
    timer = null;
  }

  function init() {
    const g = gallery();
    if (!g || thumbnails().length < 2) return;

    startTimer();

    // Pause on hover
    g.addEventListener('mouseenter', function () {
      stopTimer();
      clearTimeout(pauseTimer);
    });

    g.addEventListener('mouseleave', function () {
      if (userActed) return;
      clearTimeout(pauseTimer);
      pauseTimer = setTimeout(startTimer, PAUSE_MS);
    });

    // Stop permanently if user manually clicks a thumbnail
    thumbnails().forEach(function (btn) {
      btn.addEventListener('click', function () {
        userActed = true;
        stopTimer();
      });
    });
  }

  // Wait for the page + custom elements to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // Small delay so Horizon's media-gallery custom element can register
    setTimeout(init, 300);
  }

})();
